/**
 * Grafer over sensorhistorikken (SensorGrafer.svelte). Ren logikk: hvilke serier
 * kassa har, omregning til prosent/°C, forenkling til et passe antall punkter, og
 * brudd i linja der kassa ikke sendte data.
 */
import { jordfuktProsent, vannNivaProsent, jordGrenser, type JordGrenser } from './utils';
import type { Potte } from './database.types';
import type { SensorOppsett } from './sensorer';

export type SerieId = 'jord1' | 'jord2' | 'jord3' | 'jord4' | 'vann' | 'vann2' | 'temp' | 'rh';

export interface GrafRad {
  registrert_at: string | null;
  vann_avstand_mm: number | null;
  vann2_avstand_mm?: number | null;
  jord1: number | null;
  jord2: number | null;
  jord3: number | null;
  jord4: number | null;
  temperatur?: number | null;
  luftfuktighet?: number | null;
}

export interface SerieDef {
  id: SerieId;
  navn: string;
  enhet: '%' | '°C';
  farge: string;
  /** Fast akse (prosent) eller tilpasset dataene (temperatur). */
  fastAkse: boolean;
}

export interface Punkt {
  t: number;
  v: number;
}

export const PERIODER = [
  { id: '24t', navn: '24 t', dager: 1 },
  { id: '7d', navn: '7 d', dager: 7 },
  { id: '30d', navn: '30 d', dager: 30 },
] as const;
export type PeriodeId = (typeof PERIODER)[number]['id'];

/** Over dette er det et hull i dataene (sending hvert 5. minutt). */
export const HULL_MS = 20 * 60_000;
export const MAKS_PUNKTER = 240;

const JORD_FARGER = ['#4ade80', '#a3e635', '#34d399', '#86efac'];

/**
 * Seriene kassa faktisk har. `jordNavn` er navnet per plass (plante eller «Plass N»).
 */
export function tilgjengeligeSerier(oppsett: SensorOppsett, jordNavn: (plass: number) => string): SerieDef[] {
  const ut: SerieDef[] = [];
  oppsett.jord.forEach((har, i) => {
    if (har) {
      ut.push({ id: `jord${i + 1}` as SerieId, navn: jordNavn(i + 1), enhet: '%', farge: JORD_FARGER[i], fastAkse: true });
    }
  });
  if (oppsett.vann) {
    const navn = oppsett.vann2 ? 'Vann i venstre skuff' : 'Vann i tanken';
    ut.push({ id: 'vann', navn, enhet: '%', farge: '#60a5fa', fastAkse: true });
  }
  if (oppsett.vann2) ut.push({ id: 'vann2', navn: 'Vann i høyre skuff', enhet: '%', farge: '#22d3ee', fastAkse: true });
  if (oppsett.klima) {
    ut.push({ id: 'temp', navn: 'Temperatur', enhet: '°C', farge: '#fbbf24', fastAkse: false });
    ut.push({ id: 'rh', navn: 'Luftfuktighet', enhet: '%', farge: '#93c5fd', fastAkse: true });
  }
  return ut;
}

/** Kalibreringen grafene regner om med: jord, og tom/full per tank i mm (tom = standard). */
export interface GrafKalibrering {
  jord?: JordGrenser;
  vann?: { tom?: number; full?: number };
  vann2?: { tom?: number; full?: number };
}

type KasseKalibrering = Pick<Potte, 'vann_tom_mm' | 'vann_full_mm' | 'vann2_tom_mm' | 'vann2_full_mm' | 'jord_torr_adc' | 'jord_vaat_adc'>;

export function grafKalibrering(p: Partial<KasseKalibrering>): GrafKalibrering {
  return {
    jord: jordGrenser(p),
    vann: { tom: p.vann_tom_mm ?? undefined, full: p.vann_full_mm ?? undefined },
    vann2: { tom: p.vann2_tom_mm ?? undefined, full: p.vann2_full_mm ?? undefined },
  };
}

function verdi(r: GrafRad, id: SerieId, kal: GrafKalibrering): number | null {
  const { torr, vaat } = kal.jord ?? jordGrenser();
  switch (id) {
    case 'jord1': return jordfuktProsent(r.jord1, torr, vaat);
    case 'jord2': return jordfuktProsent(r.jord2, torr, vaat);
    case 'jord3': return jordfuktProsent(r.jord3, torr, vaat);
    case 'jord4': return jordfuktProsent(r.jord4, torr, vaat);
    case 'vann': return vannNivaProsent(r.vann_avstand_mm, kal.vann?.tom, kal.vann?.full);
    case 'vann2': return vannNivaProsent(r.vann2_avstand_mm, kal.vann2?.tom, kal.vann2?.full);
    case 'temp': return Number.isFinite(r.temperatur) ? (r.temperatur as number) : null;
    case 'rh': return Number.isFinite(r.luftfuktighet) ? (r.luftfuktighet as number) : null;
  }
}

/** Rå punkter for én serie, sortert på tid. Rader uten verdi hoppes over. */
export function seriePunkter(rader: GrafRad[], id: SerieId, kal: GrafKalibrering = {}): Punkt[] {
  const ut: Punkt[] = [];
  for (const r of rader) {
    if (!r.registrert_at) continue;
    const v = verdi(r, id, kal);
    if (v === null || !Number.isFinite(v)) continue;
    ut.push({ t: Date.parse(r.registrert_at), v });
  }
  return ut.sort((a, b) => a.t - b.t);
}

/**
 * Forenkler til maks `maks` punkter ved å snitte like lange tidsbøtter. Tomme bøtter
 * blir hull. Beholder dermed både nivået og hullene, uten 8000 punkter i SVG-en.
 */
export function forenkle(punkter: Punkt[], fra: number, til: number, maks = MAKS_PUNKTER): Punkt[] {
  if (punkter.length <= maks) return punkter;
  const bredde = (til - fra) / maks;
  const sum = new Array<number>(maks).fill(0);
  const antall = new Array<number>(maks).fill(0);
  const tSum = new Array<number>(maks).fill(0);
  for (const p of punkter) {
    const i = Math.min(maks - 1, Math.max(0, Math.floor((p.t - fra) / bredde)));
    sum[i] += p.v;
    tSum[i] += p.t;
    antall[i]++;
  }
  const ut: Punkt[] = [];
  for (let i = 0; i < maks; i++) {
    if (antall[i]) ut.push({ t: tSum[i] / antall[i], v: sum[i] / antall[i] });
  }
  return ut;
}

/** Deler i sammenhengende biter der avstanden mellom punkter er over `hull`. */
export function delVedHull(punkter: Punkt[], hull = HULL_MS): Punkt[][] {
  const biter: Punkt[][] = [];
  let bit: Punkt[] = [];
  for (const p of punkter) {
    if (bit.length && p.t - bit[bit.length - 1].t > hull) {
      biter.push(bit);
      bit = [];
    }
    bit.push(p);
  }
  if (bit.length) biter.push(bit);
  return biter;
}

/** Hull-grensen skaleres med forenklingen, så snittpunkter ikke deles opp. */
export function hullGrense(fra: number, til: number, antallRaa: number, maks = MAKS_PUNKTER): number {
  const boette = antallRaa > maks ? (til - fra) / maks : 0;
  return Math.max(HULL_MS, boette * 2.5);
}

/** Aksegrenser: 0–100 for prosent, ellers dataene med litt luft (minst 4 grader spenn). */
export function akse(def: SerieDef, punkter: Punkt[]): { min: number; max: number } {
  if (def.fastAkse) return { min: 0, max: 100 };
  if (!punkter.length) return { min: 15, max: 30 };
  let min = Math.min(...punkter.map((p) => p.v));
  let max = Math.max(...punkter.map((p) => p.v));
  if (max - min < 4) {
    const midt = (max + min) / 2;
    min = midt - 2;
    max = midt + 2;
  }
  return { min: Math.floor(min - 1), max: Math.ceil(max + 1) };
}

/** Punktet nærmest et tidspunkt (for fingeren over grafen). */
export function naermeste(punkter: Punkt[], t: number): Punkt | null {
  let beste: Punkt | null = null;
  for (const p of punkter) {
    if (!beste || Math.abs(p.t - t) < Math.abs(beste.t - t)) beste = p;
  }
  return beste;
}

const LAGRING = 'plantepotte:grafvalg:v1';

export function lesGrafvalg(): { serier: SerieId[] | null; periode: PeriodeId | null } {
  try {
    const raa = JSON.parse(localStorage.getItem(LAGRING) ?? 'null');
    return { serier: Array.isArray(raa?.serier) ? raa.serier : null, periode: raa?.periode ?? null };
  } catch {
    return { serier: null, periode: null };
  }
}

export function lagreGrafvalg(serier: SerieId[], periode: PeriodeId): void {
  try {
    localStorage.setItem(LAGRING, JSON.stringify({ serier, periode }));
  } catch {
    // privat modus o.l.: valget huskes bare denne gangen
  }
}
